import { isEqual } from 'lodash';
import { hasKey } from '@/utils/resultsHelper';
import { translatedError } from '@/utils/errors';
import { findWithUUID } from '@/utils/uuidHelper';
import fields, { globalFieldLengths } from '@/constants/fields';
import { removeUUIDsFromQueries } from './state/state';
import validateClauses from '../clauses/helper';

const validateQuery = (query, errors, isRead = false) => {
  const context = {
    entity: 'query', queryUUID: query.uuid, queryName: query.name,
  };

  if (hasKey(query, 'name') && query.name.length > globalFieldLengths.name) {
    const title = 'save.cohort.validation.query.name.title';
    const message = 'validation.max.length';

    const error = translatedError(context, title, message, [fields.NAME], { maximum: globalFieldLengths.name });
    error.translation.titleArgs = { name: query.name };
    errors.push(error);
  }

  if (query.clauses) {
    validateClauses(query, query.clauses, errors, context, isRead);
  }
};

const queryDiff = (current, previous) => {
  if (!isEqual(current.clauses, previous.clauses)) {
    return current;
  }

  return ({
    id: current.id,
    name: current.name,
    description: current.description,
  });
};

const newAndDeletedQueries = (current, previous) => {
  const queries = [];
  let currentUUIDs = current.map((query) => query.uuid);
  const previousUUIDs = previous.map((query) => query.uuid);
  const newUUIDs = currentUUIDs.filter((uuid) => !previousUUIDs.includes(uuid));
  const deletedUUIDs = previousUUIDs.filter((uuid) => !currentUUIDs.includes(uuid));

  newUUIDs.forEach((newUuid) => {
    queries.push(findWithUUID(current, newUuid));
    currentUUIDs = currentUUIDs.filter((uuid) => uuid !== newUuid);
  });

  deletedUUIDs.forEach((deletedUuid) => {
    const query = findWithUUID(previous, deletedUuid);
    queries.push({
      id: query.id,
      uuid: deletedUuid,
      code: query.code,
      toBeDeleted: true,
    });
    currentUUIDs = currentUUIDs.filter((uuid) => uuid !== deletedUuid);
  });

  return { uuids: currentUUIDs, queries };
};

const mapQueries = (current, previous, uuids) => (
  uuids.map((uuid) => {
    const currentQueries = findWithUUID(current, uuid);
    const previousQueries = findWithUUID(previous, uuid);
    if (isEqual(currentQueries, previousQueries)) { return null; }

    return ({
      current: currentQueries,
      previous: previousQueries,
    });
  }).filter((query) => query !== null)
);

const checkQueriesForDifferences = (current, previous) => {
  const { uuids, queries } = newAndDeletedQueries(current, previous);
  const differences = mapQueries(current, previous, uuids);
  const diff = differences.map((query) => queryDiff(query.current, query.previous));

  return [...queries, ...diff];
};

export const queriesDiff = (current, previous) => {
  const queries = isEqual(current, previous) ? [] : checkQueriesForDifferences(current, previous);
  return queries.length > 0 ? removeUUIDsFromQueries({ queries }) : null;
};

export default validateQuery;
